import { useQueryClient } from '@tanstack/react-query';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from 'components';
import { Button } from 'components/_form';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TeamAddModal } from './components/TeamAddModal';
import TeamsAndGroupsTable from './components/TeamsAndGroupsTable';
import { ITeam } from 'types/team';
import { createTeam, editTeam } from 'api/teams';
import { teamsKeys } from 'api/queryKeys';
import { useTeamsQuery } from 'hooks/queries/useTeamsQuery';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { toast } from 'react-toastify';
import { ITeamForm } from 'types/forms/team';
import { expandSelectedAreaIdsWithAncestors } from 'components/_form/NestedSelect/utilities';
import { buildMembersTeamsAttributes } from 'utilities/teamMembers';
import {
  parentToChildrenAreasStructure,
  parseParentToChildrenAreasStructureToOptions
} from 'utilities/areas';

export const TeamsAndGroups = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: mainCompany } = useMainCompanyQuery();
  const companyId = mainCompany?.id ?? '';
  const { data: teams = [] } = useTeamsQuery();
  const { data: areasData = [] } = useAreasQuery();
  const areaOptionsTree = useMemo(
    () =>
      parseParentToChildrenAreasStructureToOptions(
        parentToChildrenAreasStructure(areasData)
      ),
    [areasData]
  );
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [selectedItemData, setSelectedItemData] = useState<ITeam | undefined>();

  const onSubmit = async (data: ITeamForm['team']) => {
    try {
      const { leader, manager, areas: selectedAreas, logo, members, ...restData } =
        data;

      const payload = {
        team: {
          ...restData,
          leader_id: leader?.value,
          manager_id: manager?.value,
          area_ids: expandSelectedAreaIdsWithAncestors(
            areaOptionsTree,
            selectedAreas ?? []
          ),
          members_teams_attributes: buildMembersTeamsAttributes(
            selectedItemData?.members_teams,
            members?.map((member) => ({ value: member.value }))
          ),
          logo: logo || null
        }
      };

      if (selectedItemData) {
        const { data: updatedTeam } = await editTeam(String(selectedItemData.id), payload);
        queryClient.setQueryData<ITeam[]>(teamsKeys.list(companyId), (old = []) =>
          old.map((t) => (t.id === updatedTeam.id ? updatedTeam : t))
        );
        setOpenAddModal(false);
        setSelectedItemData(undefined);
        toast.success(t('common.success'));
      } else {
        const { data: newTeam } = await createTeam(payload);
        queryClient.setQueryData<ITeam[]>(teamsKeys.list(companyId), (old = []) =>
          [...old, newTeam]
        );
        setOpenAddModal(false);
        toast.success(t('common.success'));
      }
    } catch (e) {
      toast.error(t('anErrorOccurred'));
    }
  };

  return (
    <>
      {(openAddModal || !!selectedItemData) && (
        <TeamAddModal
          isOpen={openAddModal}
          onCancelClick={() => {
            setOpenAddModal(false);
            setSelectedItemData(undefined);
          }}
          onSubmit={onSubmit}
          selectedItemData={selectedItemData}
        />
      )}
      <Flex justifyContent="flex-end" width="100%" style={{ gap: '10px' }}>
        <Button
          variant="eucalyptus"
          onClick={() => {
            setOpenAddModal(true);
          }}
        >
          <FontAwesomeIcon icon={faPlusCircle} /> {t('buttons.add')}
        </Button>
      </Flex>
      <TeamsAndGroupsTable
        teams={teams}
        setSelectedItemData={setSelectedItemData}
        setOpenAddModal={setOpenAddModal}
      />
    </>
  );
};
